"use client"

import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  ChevronsUpDown,
  LogOut,
  Moon,
  Settings,
  Sun,
  User,
  Info,
  Crown,
} from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/lib/auth"
import { useTheme } from "@/components/theme-provider"
import { useTranslation } from "react-i18next"
import { supabase } from "@/lib/supabase"
import { Progress } from "@/components/ui/progress"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"

export function NavUser() {
  const { isMobile, setOpenMobile } = useSidebar()
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Créditos (período actual)
  const [loadingCredits, setLoadingCredits] = useState(true)
  const [granted, setGranted] = useState(0)
  const [consumed, setConsumed] = useState(0)
  const [remaining, setRemaining] = useState(0)
  const [percent, setPercent] = useState(0)

  useEffect(() => {
    let mounted = true
    const fetchCredits = async () => {
      if (!user) { if (mounted) setLoadingCredits(false); return }
      setLoadingCredits(true)

      // 1) Leer saldo actual y acumulados desde usage_accounts
      const { data: ua } = await supabase
        .from('usage_accounts')
        .select('current_balance, lifetime_granted, lifetime_consumed')
        .eq('user_id', user.id)
        .maybeSingle()

      // 2) Leer membresía actual para conocer los créditos mensuales (si existe)
      const { data: sub } = await supabase
        .from('user_subscriptions')
        .select('membership:memberships(name)')
        .eq('user_id', user.id)
        .maybeSingle()

      const rawMembership = (sub as any)?.membership
      const planName = Array.isArray(rawMembership) ? rawMembership?.[0]?.name : rawMembership?.name
      // No pedimos monthly_credits para evitar errores si la columna no existe. Usamos mapeo por nombre.
      const monthlyCreditsFromPlan = undefined as number | undefined
      const defaultPlanCredits = planName === 'Pro+' || planName === 'PRO +' ? 7500
        : planName === 'Pro Master' ? 15000
        : planName === 'Pro' ? 2500
        : 0

      const remainingNow = ua?.current_balance ?? 0
      // Si no hay plan detectado (default 0), mostrar al menos el saldo como "granted" para evitar '—'
      const grantedTotal = (monthlyCreditsFromPlan ?? defaultPlanCredits) || remainingNow
      const consumedNow = grantedTotal > 0 ? Math.max(0, grantedTotal - remainingNow) : (ua?.lifetime_consumed ?? 0)
      const percentUsed = grantedTotal > 0 ? Math.min(100, (consumedNow / grantedTotal) * 100) : 0

      if (mounted) {
        setGranted(grantedTotal)
        setConsumed(consumedNow)
        setRemaining(remainingNow)
        setPercent(percentUsed)
        setLoadingCredits(false)
      }
    }
    fetchCredits()
    const handler = () => fetchCredits()
    window.addEventListener('credits:refresh', handler)
    return () => { mounted = false; window.removeEventListener('credits:refresh', handler) }
  }, [user])

  const handleNavigate = (path: string) => {
    navigate(path)
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark")
  }

  const userName = user?.user_metadata?.full_name || "Usuario"
  const userEmail = user?.email || "usuario@ejemplo.com"
  const userAvatar = user?.user_metadata?.avatar_url || ""
  const userInitials = userName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:!p-2 h-16 py-3"
            >
              <Avatar className="h-8 w-8 rounded-lg flex-shrink-0">
                <AvatarImage src={userAvatar} alt={userName} />
                <AvatarFallback className="rounded-lg">{userInitials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                {/* Créditos compactos encima del nombre */}
                <div className="mb-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-sidebar-foreground">Créditos</span>
                    <span className="text-xs font-medium">{loadingCredits ? '...' : `${remaining}/${granted || '—'}`}</span>
                  </div>
                  <Progress value={percent} className="h-2 mt-1" />
                </div>
                <span className="truncate font-semibold">{userName}</span>
                <span className="truncate text-xs">{userEmail}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={userAvatar} alt={userName} />
                  <AvatarFallback className="rounded-lg">{userInitials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{userName}</span>
                  <span className="truncate text-xs">{userEmail}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem className="gap-3" onClick={() => handleNavigate('/settings')}>
                <Settings className="h-4 w-4" />
                {t('nav.user.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-3" onClick={() => handleNavigate('/settings?tab=membership')}>
                <Crown className="h-4 w-4" />
                {t('languageSwitcher.title')}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggleTheme} className="gap-3">
              {theme === "dark" ? (
                <>
                  <Sun className="h-4 w-4" />
                  {t('settings.preferences.theme')} - Claro
                </>
              ) : (
                <>
                  <Moon className="h-4 w-4" />
                  {t('settings.preferences.theme')} - Oscuro
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="gap-3">
              <LogOut className="h-4 w-4" />
              {t('nav.user.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      {/* Language Switcher */}
      <SidebarMenuItem className="group-data-[collapsible=icon]:hidden">
        <LanguageSwitcher variant="sidebar" />
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
